import { z } from "zod";
import { request } from "./client";
import type { Consents } from "../auth/port";

/**
 * POST /me/consents — record the T&C acceptance and marketing choice taken on
 * our /signup page, immediately after the Managed Login round-trip completes.
 *
 * ## Why this exists as a separate call rather than riding the signup
 *
 * It used to ride the signup: the port took `consents` and the adapter mapped
 * them to Cognito custom attributes in the SignUp API call, so an account could
 * not exist without a consent record. That guarantee is worth being honest about
 * losing, because Managed Login owns the signup form now and cannot carry them —
 * Cognito's hosted page renders Cognito's fields, and a T&C checkbox means
 * nothing without the actual terms linked beside it, which is why the checkbox
 * stays on our page.
 *
 * What replaces the guarantee: the server writes the record with ITS OWN
 * timestamp, keyed by the verified sub. That is better evidence than the
 * client-supplied `terms_accepted_at` it replaces — a browser can claim any
 * time it likes, and under the AU Spam Act the question is what we can prove,
 * not what we stored.
 *
 * What is genuinely weaker: there is now a window where a Cognito account exists
 * and the consent row does not (the user closed the tab mid-redirect). That is a
 * reconciliation problem, not a lost signup, and the post-confirmation trigger's
 * mailing write already defaults to OPT_OUT — so the failure direction is "we
 * don't email someone who wanted us to", never "we email someone who didn't".
 */

const consentAckSchema = z.object({ recordedAt: z.iso.datetime() });

export async function recordConsents(consents: Consents): Promise<void> {
  await request("/me/consents", consentAckSchema, {
    method: "POST",
    body: {
      acceptedTerms: consents.acceptedTerms,
      marketingOptIn: consents.marketingOptIn,
      termsVersion: consents.termsVersion,
    },
  });
}
